'use client'

import {
  createContext,
  useContext,
  type PropsWithChildren,
} from 'react'
import type {
  FlowUIComponentDefinitions,
  FlowUIComponentKey,
  FlowUIExtensionPack,
} from './types'

export interface FlowUIRegistry {
  activePackId: string
  resolve<K extends FlowUIComponentKey>(
    key: K,
    fallback: FlowUIComponentDefinitions[K]
  ): FlowUIComponentDefinitions[K]
}

export interface CreateFlowUIRegistryOptions {
  defaultPack: FlowUIExtensionPack
  packs?: FlowUIExtensionPack[]
  activePackId?: string
  enabledOverrides?: string[]
}

function shouldUseOverride(key: string, enabledOverrides?: string[]): boolean {
  if (!enabledOverrides || enabledOverrides.length === 0) {
    return true
  }

  return enabledOverrides.includes(key)
}

export function createFlowUIRegistry(
  options: CreateFlowUIRegistryOptions
): FlowUIRegistry {
  const {
    defaultPack,
    packs = [],
    activePackId = defaultPack.id,
    enabledOverrides,
  } = options
  const allPacks = [defaultPack, ...packs]
  const activePack =
    allPacks.find((pack) => pack.id === activePackId) || defaultPack

  return {
    activePackId: activePack.id,
    resolve(key, fallback) {
      const override = activePack.components?.[key]

      if (activePack.id === defaultPack.id || !override) {
        return fallback
      }

      return shouldUseOverride(key, enabledOverrides) ? override : fallback
    },
  }
}

const FlowUIRegistryContext = createContext<FlowUIRegistry | null>(null)

export function FlowUIRegistryProvider({
  registry,
  children,
}: PropsWithChildren<{ registry: FlowUIRegistry }>) {
  return (
    <FlowUIRegistryContext.Provider value={registry}>
      {children}
    </FlowUIRegistryContext.Provider>
  )
}

export function useFlowUIRegistry(): FlowUIRegistry {
  const registry = useContext(FlowUIRegistryContext)

  if (!registry) {
    throw new Error('useFlowUIRegistry must be used within a FlowUIRegistryProvider')
  }

  return registry
}
